import { Box, Button, Drawer, LoadingOverlay } from "@mantine/core";
import useExternalApiService from "helpers/ExternalApiService";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { store } from 'stores/Store';
import styles from './map.module.scss'
import MapCanvas, { MapCanvasHandle, MapState } from "./components/MapCanvas";
import MiniMenu from "./components/MiniMenu";
import SlideUpMenus from 'Modules/SlideUpMenus';
import RoutesTab from 'Modules/PractitionerBaseballCard/components/RoutesTab';
import RoutingModal from './components/Routing';
import Header, { ViewType } from "./components/Header";
import FunnelModal from "./components/FunnelModal";
import { DEFAULT_VALUES, FilterControl, isFilterActive } from "./components/FunnelModal/FiltersPanel";
import { ColorType } from "./components/FunnelModal/ColorsPanel";
import ColorLabel, { valueToColor, valueToGradientColor } from "./components/ColorLabel";
import { todayPlusFormatted } from "helpers/date";
import { validateAddressForGoogleMaps } from 'helpers/addressValidation';
import ListView from "./components/ListView";
import { SPECIALTY_COLOR_MAP, STATUS_COLOR_MAP } from "./components/FunnelModal/consts";
import { useLazyLocalStorage } from "hooks/useLazyLocalStorage";
import SearchModal from "./components/SearchModal";
import LocationBaseballCardModal from "./components/LocationBaseballCardModal";
import LeadForm from "Modules/CreateLead/LeadForm";
import LeadDrawer from "components/LeadDrawer";

export const DEFAULT_PIN_COLOR = "#7E7E7E";

export interface Pin {
  id: number | string;
  latLon: [number, number];
  color?: string;
}

const apiUrl = process.env.REACT_APP_EAI_URL;

const MapComponent = observer(() => {
  const [searchParams] = useSearchParams();
  const { postData } = useExternalApiService(apiUrl);

  const [isPinsLoading, setIsPinsLoading] = useState(false);
  const [initialPinsLoading, setInitialPinsLoading] = useState(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [practitionersPins, setPractitionersPins] = useState<Pin[]>([]);
  const [initialMapState, setInitialMapState] = useState<MapState>({
    latLon: [0, 0],
    focalZoom: 6,
  });
  const [onViewPracititionersIds, setOnViewPractitionersIds] = useState<Set<number | string>>(new Set());
  const [onViewPractSetLength, setOnViewPractSetLength] = useState(0);
  const [, setFetchedPractitionersIds] = useState<(number | string)[]>([]);
  const loadedPractitionersRef = useRef<any[]>();
  const [selectedPractitioner, setSelectedPractitioner] = useState<any>();
  const [isSlideUpMenusOpen, setIsSlideUpMenusOpen] = useState(false);
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);
  const [isRoutingModalOpen, setIsRoutingModalOpen] = useState(false);
  const setHasRouteChangedRef = useRef<((value: boolean) => void) | null>(null);
  const navigate = useNavigate();
  const [routeData, setRouteData] = useState<number[][]>();
  const [routePins, setRoutePins] = useState<Pin[]>([]);
  const [routeStartEndPins, setRouteStartEndPins] = useState<Pin[]>([]);
  const [isFunnelModalOpen, setIsFunnelModalOpen] = useState(false);
  const [filterControl, setFilterControl] = useLazyLocalStorage<FilterControl>
    ("map-filters", DEFAULT_VALUES);
  const [colorControl, setColorControl] = useLazyLocalStorage<ColorType>
    ("map-colors");
  const [isColorLabelOnView, setIsColorLabelOnView] = useState(true);
  const [currentView, setCurrentView] = useState<ViewType>("map");
  const [onViewBoxPins, setOnViewBoxPins] = useState<any[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResultPins, setSearchResultPins] = useState<Pin[]>([]);
  const [searchResulLocations, setSearchResultLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<any | null>(null);
  const [newLeadInfo, setNewLeadInfo] = useState<any | null>(null);
  const [isRedoSearchVisible, setIsRedoSearchVisible] = useState(false);
  const mapCanvasRef = useRef<MapCanvasHandle>(null);

  const isFunnelActive = !!(colorControl || isFilterActive(filterControl));
  const isSelectedPractitionerLocation = (typeof selectedPractitioner?.id === "string")

  const processPractitionersOverlapping = (data: { i: number, l: [number, number] }[]): Pin[] => {
    const newProcessedFeatures: Pin[] = [];

    if (!data || data.length === 0) return [];
    const offsetDistance = 0.0001;

    const groups: Record<string, number[]> = {};
    data.forEach((feature, featureIndex: number) => {
      const [x, y] = feature.l;
      const key = `${x.toFixed(4)},${y.toFixed(4)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(featureIndex);
    });
    Object.values(groups).forEach((indices) => {
      if (indices.length <= 1) {
        newProcessedFeatures.push({
          id: data[indices[0]].i,
          latLon: data[indices[0]].l,
        })
        return;
      }
      const numPins = indices.length;
      const cols = Math.ceil(Math.sqrt(numPins));
      const rows = Math.ceil(numPins / cols);
      indices.forEach((featureIndex, groupIndex) => {
        const col = groupIndex % cols;
        const row = Math.floor(groupIndex / cols);
        const baseCoords = data[featureIndex].l as [number, number];
        const offsetX = (col - (cols - 1) / 2) * offsetDistance;
        const offsetY = (row - (rows - 1) / 2) * offsetDistance;
        const offsetCoords: [number, number] = [
          baseCoords[0] + offsetX,
          baseCoords[1] + offsetY,
        ];
        newProcessedFeatures.push({
          id: data[featureIndex].i,
          latLon: offsetCoords,
        });
      });
    });

    return newProcessedFeatures;
  }

  const fetchPins = async (f?: FilterControl): Promise<Pin[] | undefined> => {
    try {
      let filterBody: { [k: string]: any } = {};
      if (f?.status?.length === STATUS_COLOR_MAP.size) {
        // null to deactivate filter
        filterBody["status"] = null;
      } else {
        filterBody["status"] = f?.status?.map((s) => s.replace("BLANK", " - "))
      }
      if (f?.specialtyType?.length === SPECIALTY_COLOR_MAP.size) {
        // null to deactivate filter
        filterBody["specialty"] = null;
      } else {
        filterBody["specialty"] = f?.specialtyType?.map((s) => s.replace("BLANK", ""))
      }
      if (f?.maxDaysSinceLastCheckIn && f?.isCheckInFilterActive) {
        const startDate = todayPlusFormatted(-f.maxDaysSinceLastCheckIn || 0);
        const endDate = todayPlusFormatted(f?.minDaysSinceLastCheckIn
          ? -f?.minDaysSinceLastCheckIn + 1 : 1);
        filterBody["start_days_since_check_in"] = startDate;
        filterBody["end_days_since_check_in"] = endDate;
      }

      const data = await postData('/api/get_practitioners_for_chat_map', {
        "fmc_email": store.impersonatedUser,
        onlyGeoValidated: true,
        limit: 999999999,
        alias: store.impersonatedUserAlias,
        ...(filterBody),
      });
      const processedPractitionersPins = processPractitionersOverlapping(data.p);
      setPractitionersPins(processedPractitionersPins);

      updatePinsColor(loadedPractitionersRef.current || [], colorControl, processedPractitionersPins);

      return processedPractitionersPins;
    } catch (error) {
      console.error('Error fetching practitioners:', error);
    }
  };

  const updatePinsColor = (practitioners: any[], c?: ColorType, fetchedPins?: Pin[]) => {
    const pins = fetchedPins ? [...fetchedPins] : [...practitionersPins];
    if (c === "last-check-in" || c === "until-follow-up") {
      practitioners?.forEach((pract) => {
        const pinIndex = pins.findIndex((p) => p.id === pract.id);
        if (pinIndex >= 0) {
          const daysSinceLastCheckIn = (
            new Date().getTime() - new Date(pract?.check_in_created_at).getTime()
          ) / (24 * 60 * 60 * 1000);
          const daysUntilFollowUp = (
            new Date().getTime() - new Date(pract?.check_in_follow_up_date).getTime()
          ) / (24 * 60 * 60 * 1000);
          pins[pinIndex] = {
            ...(pins[pinIndex]),
            color: colorControl === "last-check-in" ?
              valueToGradientColor(daysSinceLastCheckIn)
              : valueToGradientColor(daysUntilFollowUp)
          }
        }
      });
      setPractitionersPins(pins);
    } else if (c === "specialty-type" || c === "status") {
      practitioners?.forEach((pract) => {
        const pinIndex = pins.findIndex((p) => p.id === pract.id);
        if (pinIndex >= 0) {
          pins[pinIndex] = {
            ...(pins[pinIndex]),
            color: colorControl === "specialty-type" ?
              valueToColor(pract?.specialty, c)
              : valueToColor(`${pract?.stage} - ${pract?.status}`, c)
          }
        }
      });
      setPractitionersPins(pins);
    } else {
      setPractitionersPins(
        pins.map((p) => ({
          ...p,
          color: DEFAULT_PIN_COLOR,
        }))
      );
    }
  }

  const loadPractitionersByIds = (ids: (number | string)[]) => {
    if (ids.length === 0) return;

    setIsPinsLoading(true);
    postData('/api/get_practitioners_by_ids', {
      "fmc_email": store.impersonatedUser,
      practitioner_ids: ids,
    }).then((r) => {
      if (r?.practitioners && r?.practitioners.length > 0) {
        const preSelectedPractitionerId = parseInt(searchParams.get("id") || "");
        store.setPractitionersFilteredByFMC([
          ...store.practitionersFilteredByFMC,
          ...(r?.practitioners || []),
        ]);
        loadedPractitionersRef.current = [
          ...(loadedPractitionersRef.current || []),
          ...(r.practitioners),
        ];
        if (preSelectedPractitionerId) {
          const p = loadedPractitionersRef.current.find(
            (lp) => lp.id === preSelectedPractitionerId
          );
          if (p) {
            setSelectedPractitioner(p)
          }
        }
      }
    }).finally(() => setIsPinsLoading(false));
  };

  const handleAddToRoute = (practitioner: any) => {
    store.addToRoute(practitioner);
    if (setHasRouteChangedRef.current) {
      setHasRouteChangedRef.current(true);
    }
    setSelectedPractitioner(undefined);
    setIsSlideUpMenusOpen(true);
  };

  const handleGoRoute = (opt: string) => {
    if (!selectedPractitioner) return;
    // Use the same address structure as baseball card component
    let defaultAddress = selectedPractitioner?.addresses
      ?.find((a: any) => a.id === selectedPractitioner?.default_address);

    if (!isSelectedPractitionerLocation) {
      if (!defaultAddress) {
        console.log('No default address available for navigation');
        return;
      }
    } else {
      defaultAddress = {
        ...selectedPractitioner,
        latitude: selectedPractitioner?.latLon[0],
        longitude: selectedPractitioner?.latLon[1],
      }
    }

    const address = defaultAddress.address || '';
    const coordinates = `${defaultAddress.latitude},${defaultAddress.longitude}`;

    switch (opt) {
      case 'google':
        // Validate and clean address for Google Maps search
        if (address) {
          const { isValid, cleanedAddress } = validateAddressForGoogleMaps(address);
          if (isValid && cleanedAddress) {
            // Use cleaned address search if valid
            const encodedAddress = encodeURIComponent(cleanedAddress);
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
          } else if (defaultAddress.latitude && defaultAddress.longitude) {
            // Fallback to coordinates if address is malformed
            window.open(`https://www.google.com/maps/search/?api=1&query=${coordinates}`, '_blank');
          } else {
            console.log('No valid address or coordinates for Google Maps navigation');
          }
        } else if (defaultAddress.latitude && defaultAddress.longitude) {
          // No address at all, use coordinates
          window.open(`https://www.google.com/maps/search/?api=1&query=${coordinates}`, '_blank');
        } else {
          console.log('No valid address or coordinates for Google Maps navigation');
        }
        break;
      case 'waze':
        if (defaultAddress.latitude && defaultAddress.longitude) {
          window.open(`https://waze.com/ul?ll=${defaultAddress.latitude},${defaultAddress.longitude}&navigate=yes`, '_blank');
        } else {
          console.log('No coordinates available for Waze navigation');
        }
        break;
      case 'apple':
        // For Apple Maps, try cleaned address first, then coordinates
        if (address) {
          const { isValid, cleanedAddress } = validateAddressForGoogleMaps(address);
          if (isValid && cleanedAddress) {
            const encodedAddress = encodeURIComponent(cleanedAddress);
            window.open(`maps://?q=${encodedAddress}`, '_blank');
          } else if (defaultAddress.latitude && defaultAddress.longitude) {
            window.open(`maps://?q=${coordinates}`, '_blank');
          } else {
            console.log('No valid address or coordinates for Apple Maps navigation');
          }
        } else if (defaultAddress.latitude && defaultAddress.longitude) {
          window.open(`maps://?q=${coordinates}`, '_blank');
        } else {
          console.log('No valid address or coordinates for Apple Maps navigation');
        }
        break;
    }
    setIsGoRouteModalOpen(false);
  };

  const handleViewChange = useCallback((v: ViewType) => {
    if (v === "list") {
      const _onViewBoxPins = mapCanvasRef.current?.getViewBoxPins();
      if (_onViewBoxPins) {
        setOnViewBoxPins(_onViewBoxPins);
      }
      setIsColorLabelOnView(false);
      setSelectedPractitioner(null);
    }
    setCurrentView(v);
  }, [loadedPractitionersRef]);

  const fetchSearchResults = () => {
    const center = mapCanvasRef.current?.getViewBoxCenter();
    const viewHeight = mapCanvasRef.current?.getViewBoxRadius();
    if (!viewHeight || !center) {
      console.error("Internal error");
      return;
    }
    setIsPinsLoading(true);
    setSelectedPractitioner(null);
    postData('/api/google_places_text_search_by_bounds', {
      query: searchTerm,
      location: center,
      radius: (viewHeight / 2),
      language: "en",
    }).then((response: any) => {
      const pins: Pin[] = response?.results?.map((r: any) => ({
        id: r?.place_id,
        latLon: [r?.geometry?.location?.lat, r?.geometry?.location?.lng],
      } as Pin)) || [];
      const locations = response?.results?.map((r: any) => ({
        id: r?.place_id,
        name: r?.name,
        primary_name: r?.name,
        latLon: [r?.geometry?.location?.lat, r?.geometry?.location?.lng],
        formatted_address: r?.formatted_address,
      }));
      setSearchResultLocations(locations);
      setSearchResultPins(pins);
    }).finally(() => {
      setIsPinsLoading(false)
      setIsRedoSearchVisible(false);
    });
  }

  useEffect(() => {
    updatePinsColor(loadedPractitionersRef.current || [], colorControl);
  }, [loadedPractitionersRef.current, colorControl]);

  useEffect(() => {
    if (onViewPracititionersIds && onViewPractSetLength > 0) {
      setFetchedPractitionersIds((prev) => {
        const notLoadedPractitionersIds = [...onViewPracititionersIds]
          .filter((onViewId) => !prev.includes(onViewId));
        loadPractitionersByIds(notLoadedPractitionersIds);
        return [
          ...prev,
          ...notLoadedPractitionersIds,
        ]
      })
    }
  }, [onViewPractSetLength]);

  useEffect(() => {
    if (store?.currentDirections) {
      if (store?.currentDirections.length > 0) {
        setRouteData(store?.currentDirections);
      } else {
        setRouteData(undefined);
      }
    } else {
      setRouteData(undefined);
    }
  }, [store.currentDirections]);

  useEffect(() => {
    const pins: Pin[] = [];

    store.selectedPractitioners
      .filter((s) => !s.isFirstLocation && !s.isLastLocation)
      .forEach((p) => {
        const lon = parseFloat(String(p.longitude));
        const lat = parseFloat(String(p.latitude));
        pins.push({
          id: p.id,
          latLon: [lat, lon]
        })
      });

    const r: Pin[] = [];
    store.selectedPractitioners.forEach((item, index) => {
      if (item.isFirstLocation) {
        r.push({
          id: item.id,
          latLon: [item.latitude, item.longitude],
        });
      }
      if (item.isLastLocation) {
        r.push({
          id: item.id,
          latLon: [item.latitude, item.longitude],
        });
      }
    });
    setRouteStartEndPins(r);
    setRoutePins(pins);

    // Ensure all route practitioners are loaded, even if filtered out
    const routePractitionerIds = store.selectedPractitioners
      .filter(p => p.id && !p.isFirstLocation && !p.isLastLocation)
      .map(p => p.id);

    const unloadedRouteIds = routePractitionerIds.filter(
      id => !loadedPractitionersRef.current?.find(p => p.id === id)
    );

    if (unloadedRouteIds.length > 0) {
      loadPractitionersByIds(unloadedRouteIds);
    }
  }, [store.selectedPractitioners]);

  useEffect(() => {
    fetchPins(filterControl).then((practitionersPins) => {

      const lat = searchParams.get("lat");
      const lon = searchParams.get("lon");
      if (practitionersPins && practitionersPins.length > 0 &&
        (!store.initialLatLon || !(lat && lon) || (!initialMapState.latLon))
      ) {
        const firstValidPin = practitionersPins[0];
        setInitialMapState({
          ...initialMapState,
          latLon: firstValidPin.latLon,
        });
      }
    }).finally(() => setInitialPinsLoading(false));
  }, [filterControl]);


  useEffect(() => {
    if (searchParams) {
      const lat = searchParams.get("lat");
      const lon = searchParams.get("lon");
      if (lat && lon) {
        setInitialMapState({
          ...initialMapState,
          latLon: [parseFloat(lat), parseFloat(lon)],
          focalZoom: 15,
        })
      } else {
        if (store.initialLatLon) {
          setInitialMapState({
            ...initialMapState,
            latLon: store.initialLatLon,
            focalZoom: 6,
          })
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchTerm) {
      fetchSearchResults();
    } else {
      setSearchResultLocations([]);
      setSearchResultPins([]);
    }
  }, [searchTerm]);


  return (
    <div className={styles.container}>
      <LoadingOverlay
        visible={!isMapLoaded || isPinsLoading}
        zIndex={1000} />
      <LocationBaseballCardModal
        onClose={() => {
          setSelectedLocation(null);
        }}
        onCreateLead={(li) => {
          setNewLeadInfo(li);
        }}
        onCenterOnMap={(latLon) => {
          setSelectedLocation(null);
          mapCanvasRef.current?.centerMap(latLon);
        }}
        onFindOnMap={(locationInfo) => {
          setIsGoRouteModalOpen(true);
        }}
        onPhoneClicked={(phone) => {
          window.open(`tel:${phone}`, '_blank');
        }}
        placeId={selectedLocation?.id} />
      <LeadDrawer
        practitionerInfo={newLeadInfo}
        onClose={() => {
          setNewLeadInfo(null);
        }} />
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => {
          setIsSearchModalOpen(false);
        }}
        onSearch={(s) => {
          setSearchTerm(s);
          setIsSearchModalOpen(false);
        }} />
      <FunnelModal
        isOpen={isFunnelModalOpen}
        initFilterControl={filterControl}
        initColor={colorControl}
        onApplyFilter={(f) => {
          setFilterControl(f);
        }}
        onApplyColor={(c) => {
          setColorControl(c);
          updatePinsColor(loadedPractitionersRef?.current || [], c);
        }}
        onClose={() => setIsFunnelModalOpen(false)} />
      <Header
        isFunnelActive={isFunnelActive}
        isFilterActive={isFilterActive(filterControl)}
        isSearchModeActive={!!searchTerm}
        onFunnelClicked={() => {
          setIsFunnelModalOpen(true);
        }}
        onSearchModeClicked={() => {
          setIsSearchModalOpen((prev) => !prev);
        }}
        onViewChange={handleViewChange} />
      <SlideUpMenus
        isOpen={isSlideUpMenusOpen}
        selectedView={currentView === "list" || selectedPractitioner ? 'baseballcard' : 'createRoute'}
        setIsOpen={setIsSlideUpMenusOpen}
        onRouteChanged={(setHasRouteChanged) => {
          setHasRouteChangedRef.current = setHasRouteChanged;
        }} />
      <RoutesTab
        isOpen={isGoRouteModalOpen}
        setIsOpen={setIsGoRouteModalOpen}
        onSelect={handleGoRoute} />
      <RoutingModal
        selectedPractitioners={store.selectedPractitioners}
        isOpen={isRoutingModalOpen}
        onClear={() => {
          store.setSelectedPractitioners([]);
          setIsRoutingModalOpen(false);
        }}
        handleClose={() => setIsRoutingModalOpen(false)}
      />

      <Box pos="absolute" bottom={0} style={{ zIndex: 1000 }}>
        <MiniMenu
          practitioner={!isSlideUpMenusOpen && selectedPractitioner}
          onSlideMenuOpen={() => {
            if (typeof selectedPractitioner.id === "string") {
              setSelectedLocation(selectedPractitioner);
            } else {
              setIsSlideUpMenusOpen(true);
            }
          }}
          onRouteMenuOpen={() => {
            setIsGoRouteModalOpen(true);
          }}
          onAddToRoute={(p) => {
            handleAddToRoute(p);
          }} />
        {colorControl && (
          <ColorLabel
            isVisible={isColorLabelOnView}
            colorControl={colorControl} />
        )}
      </Box>

      {currentView === "list" && (
        <ListView
          onPractitionerSelected={(p) => {
            store.setCurrentPractitioner(p);
            setIsSlideUpMenusOpen(true);
          }}
          practitioners={onViewBoxPins.filter(
            (pin) => loadedPractitionersRef.current?.find(
              (pract) => pract.id === pin.id
            )
          ).map((pin) => {
            const pract = loadedPractitionersRef.current?.find(
              (pract) => pract.id === pin.id
            );
            return pract;
          })}
        />
      )}

      {!initialPinsLoading && (
        <MapCanvas
          ref={mapCanvasRef}
          pins={practitionersPins}
          searchResultPins={searchResultPins}
          mapState={initialMapState}
          routeData={routeData}
          routePins={routePins}
          routeStartEndPins={routeStartEndPins}
          isColorLabelActive={!!colorControl}
          isColorLabelOnView={isColorLabelOnView}
          onColorLabeOnViewChange={(v) => setIsColorLabelOnView(v)}
          isModalOpen={isSlideUpMenusOpen}
          onMapLoaded={() => {
            setIsMapLoaded(true);
          }}
          selectedPinId={selectedPractitioner?.id}
          onPinSelected={(id) => {
            // First try to find in loaded practitioners
            let p = loadedPractitionersRef.current?.find((p) => p.id === id);

            // If not found in loaded practitioners, check if it's in the route
            if (!p && id) {
              p = store.selectedPractitioners.find((sp) => sp.id === id);
            }

            if (p) {
              setSelectedPractitioner(p);
              setIsColorLabelOnView(false);
            } else {
              // clicked on location
              if (typeof id === "string") {
                const l = searchResulLocations.find(
                  (x) => x.id === id
                );
                if (l) {
                  setSelectedPractitioner(l);
                } else {
                  setSelectedPractitioner(null);
                }
              } else {
                setSelectedPractitioner(null);
              }
            }
          }}
          onViewPins={(ids) => {
            setOnViewPractitionersIds((prev) => {
              const s = new Set([
                ...prev,
                ...ids
              ]);
              setOnViewPractSetLength(s.size);
              return s;
            });
          }}
          onPinsLassoed={(ids) => {
            if (!ids) {
              store.setSelectedPractitioners([]);
              setRouteData(undefined);
              setRoutePins([]);
            } else {
              loadedPractitionersRef.current?.filter(
                (p) => ids.includes(p.id)
              ).forEach((p) => {
                handleAddToRoute(p);
              });
            }
            setIsColorLabelOnView(false);
          }}
          onSlideUpMenuOpen={() => {
            setIsSlideUpMenusOpen(true);
            setIsColorLabelOnView(false);
          }}
          onNavigateHome={() => {
            navigate(`/`)
          }}
          onViewBoxChange={() => {
            if (searchTerm) {
              setIsRedoSearchVisible(true);
            }
          }} />
      )}
      {searchTerm && isRedoSearchVisible ? (
        <Button size="compact-xs" pos="absolute" top={50} bg="#4F7D9C"
          style={{
            zIndex: 5002,
            left: "50%",
            transform: "translateX(-50%)"
          }}
          onClick={() => {
            fetchSearchResults();
          }}>
          Redo Search
        </Button>
      ) : null}
    </div>
  );
}
)

export default MapComponent;